import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tourOpen, setTourOpen] = useState(false);

  const loadBusiness = useCallback(async () => {
    try {
      const { data } = await api.get("/business");
      setBusiness(data);
    } catch {}
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch { return null; }
  }, []);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("neec_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.role !== "superadmin") await loadBusiness();
      try { const { cacheOfflineSession } = await import("@/lib/offline/session"); await cacheOfflineSession(data); } catch { /* optional */ }
    } catch (e) {
      // Network failure (not a 401): allow the bounded offline session cached on this device (12h, trusted register, open shift).
      if (!e.response && localStorage.getItem("neec_token")) {
        try {
          const { restoreOfflineSession } = await import("@/lib/offline/session");
          const off = await restoreOfflineSession();
          if (off) { setUser(off.user); setBusiness(off.business); setLoading(false); return; }
        } catch { /* fall through to sign-in */ }
        localStorage.setItem("neec_offline_denied", "1");
      }
      localStorage.removeItem("neec_token");
    } finally {
      setLoading(false);
    }
  }, [loadBusiness]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Build B: role changes are enforced by the server on every request; this only keeps the displayed role/navigation
  // fresh. Re-read /auth/me when the tab regains focus/visibility, throttled to one request per 30s.
  useEffect(() => {
    let last = 0;
    const onFocus = () => {
      if (document.visibilityState === "hidden" || !localStorage.getItem("neec_token")) return;
      const now = Date.now();
      if (now - last < 30000) return;
      last = now;
      refreshUser();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onFocus); };
  }, [refreshUser]);

  const login = async (email, password, code) => {
    const { data } = await api.post("/auth/login", { email, password, code });
    if (data.twofa_required) return { twofaRequired: true };
    localStorage.setItem("neec_token", data.token);
    setUser(data.user);
    if (data.user.role !== "superadmin") await loadBusiness();
    return data.user;
  };

  // Quick Login / user switch: wipe every cached account object before the new identity lands.
  const setSession = async (token, u) => {
    localStorage.setItem("neec_token", token);
    setBusiness(null);
    setTourOpen(false);
    setUser(u);
    if (u.role !== "superadmin") await loadBusiness();
    const fresh = await refreshUser();
    return fresh || u;
  };

  const demoLogin = async () => {
    const { data } = await api.post("/auth/demo-login");
    localStorage.setItem("neec_token", data.token);
    setUser(data.user);
    await loadBusiness();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    try { const { clearOfflineSession } = await import("@/lib/offline/session"); await clearOfflineSession(); } catch {}
    localStorage.removeItem("neec_token");
    setUser(null);
    setBusiness(null);
    setTourOpen(false);
  };

  const openTour = () => setTourOpen(true);
  const closeTour = () => setTourOpen(false);

  return (
    <AuthContext.Provider value={{
      user, business, loading, login, logout, loadBusiness, refreshUser,
      setSession, demoLogin, tourOpen, openTour, closeTour,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
